import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DOKUMENT_STATUS_LABELS,
  listDokumente,
  type DokumentStatus,
} from "@/lib/dokumente";
import {
  TimesheetInvoiceAngebotHintDialog,
  type ProjectAngebotHint,
} from "@/features/timesheetFlow/components/TimesheetInvoiceAngebotAlert";
import { TimesheetInvoiceMultiSelectDialog } from "@/features/timesheetFlow/components/TimesheetInvoiceMultiSelectDialog";
import {
  getFooterInvoiceDraftActions,
  type InvoiceDraftAction,
  type InvoiceDraftProvider,
} from "@/features/timesheetFlow/timesheetBillingUtils";
import { runTimesheetInvoiceDraftAction } from "@/features/timesheetFlow/timesheetInvoiceHandlers";
import {
  listGdInvoiceCandidates,
  type GdInvoiceCandidate,
} from "@/features/timesheetFlow/timesheetFlowApi";
import type { TimesheetRecord } from "@/features/timesheetFlow/timesheetFlowTypes";
import { useDocumentEditor } from "@/pages/dokumente/useDocumentEditor";

async function loadProjectAngebote(auftragId: number): Promise<ProjectAngebotHint[]> {
  const items = await listDokumente({
    typ: "angebot",
    auftrag_id: auftragId,
    limit: 50,
  });
  return items
    .filter((d) => d.status !== "storniert" && !d.has_newer_revision)
    .map((d) => {
      const status = d.status as DokumentStatus;
      return {
        id: d.id,
        label: String(d.display_number || d.dokument_nummer || `#${d.id}`).trim(),
        statusLabel: DOKUMENT_STATUS_LABELS[status] || String(d.status || ""),
      };
    });
}

export function useTimesheetInvoiceDraftControls({
  timesheetId,
  timesheet,
  normalizedStatus,
  canBilling,
  onReload,
  onRefresh,
  onError,
}: {
  timesheetId: number;
  timesheet: TimesheetRecord | null;
  normalizedStatus: string;
  canBilling: boolean;
  onReload: () => Promise<void>;
  onRefresh: () => void;
  onError: (message: string) => void;
}) {
  const documentEditor = useDocumentEditor();
  const [multiOpen, setMultiOpen] = useState(false);
  const [multiPrimary, setMultiPrimary] = useState<GdInvoiceCandidate | null>(null);
  const [multiCandidates, setMultiCandidates] = useState<GdInvoiceCandidate[]>([]);
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [projectAngebote, setProjectAngebote] = useState<ProjectAngebotHint[]>([]);
  /** true, wenn der Angebot-Hinweis in Multi bereits sichtbar war */
  const [skipAngebotAck, setSkipAngebotAck] = useState(false);
  const [angebotAckOpen, setAngebotAckOpen] = useState(false);
  const [pendingAfterAngebotAck, setPendingAfterAngebotAck] = useState<(() => void) | null>(
    null,
  );

  useEffect(() => {
    const auftragId = timesheet?.auftrag_id;
    if (!canBilling || auftragId == null || !Number.isFinite(Number(auftragId))) {
      setProjectAngebote([]);
      return;
    }
    let cancelled = false;
    void loadProjectAngebote(Number(auftragId))
      .then((items) => {
        if (!cancelled) setProjectAngebote(items);
      })
      .catch(() => {
        if (!cancelled) setProjectAngebote([]);
      });
    return () => {
      cancelled = true;
    };
  }, [canBilling, timesheet?.auftrag_id]);

  const footerInvoiceActions = useMemo(
    () =>
      timesheet ? getFooterInvoiceDraftActions(normalizedStatus, timesheet, canBilling) : [],
    [timesheet, normalizedStatus, canBilling],
  );

  const openGdRechnung = useCallback(
    (docId: number) => {
      documentEditor.open({ docId });
    },
    [documentEditor],
  );

  const runProviderAction = useCallback(
    async (
      provider: InvoiceDraftProvider,
      mode: InvoiceDraftAction["mode"],
      timesheetIds?: number[],
    ) => {
      if (!timesheet) return;
      setInvoiceBusy(true);
      try {
        await runTimesheetInvoiceDraftAction(timesheetId, timesheet, normalizedStatus, mode, {
          provider,
          timesheetIds,
          onReload,
          onRefresh,
          onError,
          onOpenGdRechnung: openGdRechnung,
        });
      } catch (err: unknown) {
        onError(err instanceof Error ? err.message : "Rechnungsentwurf fehlgeschlagen.");
      } finally {
        setInvoiceBusy(false);
      }
    },
    [timesheet, timesheetId, normalizedStatus, onReload, onRefresh, onError, openGdRechnung],
  );

  const withAngebotAck = useCallback(
    (next: () => void) => {
      if (!projectAngebote.length || skipAngebotAck) {
        next();
        return;
      }
      setPendingAfterAngebotAck(() => next);
      setAngebotAckOpen(true);
    },
    [projectAngebote.length, skipAngebotAck],
  );

  const startGdCreate = useCallback(async () => {
    if (!timesheet) return;
    setInvoiceBusy(true);
    try {
      const data = await listGdInvoiceCandidates(timesheetId);
      if (data.has_additional && data.candidates.length > 0) {
        setMultiPrimary(data.primary);
        setMultiCandidates(data.candidates);
        setMultiOpen(true);
        return;
      }
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Rechnungsentwurf fehlgeschlagen.");
      return;
    } finally {
      setInvoiceBusy(false);
    }
    withAngebotAck(() => {
      void runProviderAction("gd", "create", [timesheetId]);
    });
  }, [timesheet, timesheetId, onError, withAngebotAck, runProviderAction]);

  const handleInvoiceAction = useCallback(
    (action: InvoiceDraftAction) => {
      setSkipAngebotAck(false);
      setAngebotAckOpen(false);
      setPendingAfterAngebotAck(null);
      if (action.mode === "create" && (action.provider ?? "gd") === "gd") {
        void startGdCreate();
        return;
      }
      if (action.mode === "create") {
        withAngebotAck(() => {
          void runProviderAction(action.provider ?? "gd", action.mode);
        });
        return;
      }
      void runProviderAction(action.provider ?? "gd", action.mode);
    },
    [runProviderAction, startGdCreate, withAngebotAck],
  );

  const choiceDialog = (
    <>
      <TimesheetInvoiceMultiSelectDialog
        open={multiOpen}
        busy={invoiceBusy}
        primary={multiPrimary}
        candidates={multiCandidates}
        angebote={projectAngebote}
        onClose={() => {
          if (invoiceBusy) return;
          setMultiOpen(false);
          setMultiPrimary(null);
          setMultiCandidates([]);
        }}
        onConfirm={(ids) => {
          setMultiOpen(false);
          setSkipAngebotAck(true);
          void runProviderAction("gd", "create", ids).finally(() => {
            setMultiPrimary(null);
            setMultiCandidates([]);
          });
        }}
      />
      <TimesheetInvoiceAngebotHintDialog
        open={angebotAckOpen}
        busy={invoiceBusy}
        angebote={projectAngebote}
        onClose={() => {
          if (invoiceBusy) return;
          setAngebotAckOpen(false);
          setPendingAfterAngebotAck(null);
        }}
        onContinue={() => {
          const next = pendingAfterAngebotAck;
          setAngebotAckOpen(false);
          setPendingAfterAngebotAck(null);
          setSkipAngebotAck(true);
          next?.();
        }}
      />
    </>
  );

  return {
    footerInvoiceActions,
    handleInvoiceAction,
    invoiceBusy,
    choiceDialog,
  };
}
